// GAVION deal amount visibility (supabase/schemas/72_gavion_deal_amount_visibility.sql): authenticated has no SELECT
// on deals.amount, so the « deals » resource reads public.deals_visible (every deals column, amount masked for a Setter
// / Apporteur only, plus amount_hidden) and writes /deals with select=id (PostgREST's RETURNING would need the amount),
// then reads the saved row back from the view. The database keeps a hidden amount on update whatever is sent
// (guard_deals_amount); the amount is not even sent here when the row says it is hidden.
// GAVION deal package (76_): the « Forfait proposé » columns are masked like the amount; a row whose amount is hidden
// sends none of them either.
import type { DataProvider, Identifier, RaRecord } from "ra-core";
import { DEAL_PROPOSAL_COLUMNS } from "../domain/deals/dealProposal";

export const DEALS_RESOURCE = "deals";
export const DEALS_READ_VIEW = "deals_visible";

const ID_ONLY = ["id"];

/** The view's own column, and a hidden amount (never written by its viewer). */
export const writableDealData = (
  data: Record<string, unknown> | undefined,
  previous?: Record<string, unknown> | null,
): Record<string, unknown> => {
  const { amount_hidden: _hidden, ...rest } = data ?? {};
  if (previous?.amount_hidden === true || data?.amount_hidden === true) {
    const { amount: _amount, ...withoutAmount } = rest;
    return Object.fromEntries(
      Object.entries(withoutAmount).filter(
        ([k]) => !(DEAL_PROPOSAL_COLUMNS as readonly string[]).includes(k),
      ),
    );
  }
  return rest;
};

export const withDealsAmountView = <T extends DataProvider>(base: T): T => {
  const isDeals = (resource: string) => resource === DEALS_RESOURCE;
  const readBack = async (id: Identifier, fallback: RaRecord): Promise<any> => {
    try {
      return (await base.getOne(DEALS_READ_VIEW, { id })).data;
    } catch {
      return fallback;
    }
  };
  const idOnly = (meta: any) => ({ ...(meta ?? {}), columns: ID_ONLY });
  return {
    ...base,
    getList: (resource: string, params: any) =>
      base.getList(isDeals(resource) ? DEALS_READ_VIEW : resource, params),
    getOne: (resource: string, params: any) =>
      base.getOne(isDeals(resource) ? DEALS_READ_VIEW : resource, params),
    getMany: (resource: string, params: any) =>
      base.getMany(isDeals(resource) ? DEALS_READ_VIEW : resource, params),
    getManyReference: (resource: string, params: any) =>
      base.getManyReference(
        isDeals(resource) ? DEALS_READ_VIEW : resource,
        params,
      ),
    async create(resource: string, params: any): Promise<any> {
      if (!isDeals(resource)) return base.create(resource, params);
      const { data } = await base.create(resource, {
        ...params,
        data: writableDealData(params.data),
        meta: idOnly(params.meta),
      });
      return { data: await readBack(data.id, { ...params.data, ...data }) };
    },
    async update(resource: string, params: any): Promise<any> {
      if (!isDeals(resource)) return base.update(resource, params);
      const data = writableDealData(params.data, params.previousData);
      await base.update(resource, {
        ...params,
        data,
        meta: idOnly(params.meta),
      });
      return {
        data: await readBack(params.id, {
          ...(params.previousData ?? {}),
          ...data,
          id: params.id,
        }),
      };
    },
    updateMany: (resource: string, params: any) =>
      isDeals(resource)
        ? base.updateMany(resource, {
            ...params,
            data: writableDealData(params.data),
            meta: idOnly(params.meta),
          })
        : base.updateMany(resource, params),
    async delete(resource: string, params: any): Promise<any> {
      if (!isDeals(resource)) return base.delete(resource, params);
      const { data } = await base.delete(resource, {
        ...params,
        meta: idOnly(params.meta),
      });
      return { data: params.previousData ?? data };
    },
    deleteMany: (resource: string, params: any) =>
      isDeals(resource)
        ? base.deleteMany(resource, { ...params, meta: idOnly(params.meta) })
        : base.deleteMany(resource, params),
  } as T;
};
